// /src/components/UserManagement.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api/axios';
import { 
  FaUserCog, 
  FaTrash, 
  FaSpinner, 
  FaPlus,
  FaExclamationTriangle,
  FaTimes,
  FaUserShield,
  FaKey,
  FaCheck,
  FaSearch,
  FaFilter
} from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

const UserCard = ({ user, onDelete, onGeneratePin }) => {
  const [isGeneratingPin, setIsGeneratingPin] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

  const handleGeneratePin = async () => {
    setIsGeneratingPin(true);
    try {
      const response = await api.post(
        '/users/generate-pin',
        { role: user.role === 'admin' ? 'host' : 'manager' },
        { headers: { 'x-auth-token': localStorage.getItem('token') } }
      );
      onGeneratePin(response.data.pin);
    } catch (error) {
      console.error('Failed to generate PIN:', error);
    } finally {
      setIsGeneratingPin(false);
      setShowGenerateConfirm(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-all duration-300">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gray-700 rounded-lg">
              <FaUserCog className="text-indigo-400 h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{user.username}</h3>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 
                ${user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 
                  user.role === 'user' ? 'bg-blue-500/20 text-blue-400' : 
                  'bg-green-500/20 text-green-400'}`}>
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowGenerateConfirm(true)}
              className="p-2 text-indigo-400 hover:text-indigo-300 hover:bg-gray-700 
                       rounded-lg transition-colors"
              title="Generate Access PIN"
            >
              <FaKey />
            </button>
            <button
              onClick={() => onDelete(user)}
              className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 
                       rounded-lg transition-colors"
              title="Delete User"
            >
              <FaTrash />
            </button>
          </div>
        </div>

        {showGenerateConfirm && (
          <div className="mt-4 p-4 bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-300 mb-3">
              Generate access PIN for {user.role === 'admin' ? 'host' : 'manager'} role?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowGenerateConfirm(false)}
                className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-lg 
                         hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGeneratePin}
                disabled={isGeneratingPin}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg 
                         hover:bg-indigo-700 transition-colors flex items-center"
              >
                {isGeneratingPin ? (
                  <>
                    <FaSpinner className="animate-spin mr-1" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FaKey className="mr-1" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PinModal = ({ pin, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <Card className="w-full max-w-md bg-gray-800 border-gray-700">
      <CardContent className="p-6">
        <div className="text-center mb-4">
          <FaCheck className="mx-auto text-green-500 text-4xl mb-2" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Access PIN Generated
          </h3>
          <p className="text-gray-400 mb-4">
            Share this PIN with the user to complete their registration:
          </p>
          <div className="bg-gray-700 p-4 rounded-lg mb-4">
            <code className="text-2xl font-mono text-white">{pin}</code>
          </div>
          <p className="text-sm text-gray-400">
            This PIN will expire in 24 hours
          </p>
        </div>
        <div className="flex justify-center">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 
                     transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const DeleteConfirmDialog = ({ user, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <Card className="w-full max-w-md bg-gray-800 border-gray-700">
      <CardContent className="p-6">
        <div className="flex items-center mb-4">
          <FaExclamationTriangle className="text-yellow-400 mr-2 text-xl" />
          <h3 className="text-lg font-semibold text-white">Confirm Delete</h3>
        </div>
        <p className="text-gray-300 mb-6">
          Are you sure you want to delete user "{user.username}"? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 
                     transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 
                     transition-colors duration-200"
          >
            Delete
          </button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [generatedPin, setGeneratedPin] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${userToDelete._id}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setUsers(users.filter(user => user._id !== userToDelete._id));
      setUserToDelete(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError('Failed to delete user');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="flex-1 space-y-8 p-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-white">User Management</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                       text-white placeholder-gray-400 focus:border-indigo-500 
                       focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                     text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="guest">Guest</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center">
          <FaExclamationTriangle className="text-red-500 mr-2" />
          <span className="text-red-500">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-400"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Users Grid */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <FaSpinner className="animate-spin text-indigo-500 text-2xl" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6 text-center">
              <FaUserCog className="mx-auto text-4xl text-gray-600 mb-4" />
              <p className="text-gray-400">
                {searchTerm || roleFilter !== 'all' 
                  ? 'No users match your search criteria'
                  : 'No users found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredUsers.map(user => (
              <UserCard
                key={user._id}
                user={user}
                onDelete={setUserToDelete}
                onGeneratePin={setGeneratedPin}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {userToDelete && (
        <DeleteConfirmDialog
          user={userToDelete}
          onConfirm={handleDelete}
          onCancel={() => setUserToDelete(null)}
        />
      )}

      {/* PIN Display Modal */}
      {generatedPin && (
        <PinModal
          pin={generatedPin}
          onClose={() => setGeneratedPin(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;